/**
 * 1099 contractor timesheets — explicit entries separate from W-2 projectTimeEntries.
 */

import { app, db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import type {
  ContractorInvoice,
  ContractorProfile,
  ContractorTimeEntry,
  ContractorTimesheet,
} from '../types';
import { removeUndefinedDeep } from './removeUndefinedDeep';
import { isContractorW9OnFile, parseContractorW9Status } from './contractorW9';

const PROFILES = 'contractorProfiles';
const ENTRIES = 'contractorTimeEntries';
const TIMESHEETS = 'contractorTimesheets';
const INVOICES = 'contractorInvoices';

function toDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function mapProfile(snap: { id: string; data: () => Record<string, unknown> }): ContractorProfile {
  const d = snap.data();
  return {
    userId: snap.id,
    businessName: typeof d.businessName === 'string' ? d.businessName : undefined,
    defaultHourlyRate: typeof d.defaultHourlyRate === 'number' ? d.defaultHourlyRate : 0,
    defaultDayRate: typeof d.defaultDayRate === 'number' ? d.defaultDayRate : undefined,
    invoiceBillingMode:
      d.invoiceBillingMode === 'day_rate_required' ? 'day_rate_required' : d.invoiceBillingMode === 'hourly' ? 'hourly' : undefined,
    qbVendorName: typeof d.qbVendorName === 'string' ? d.qbVendorName : undefined,
    w9Status: isContractorW9OnFile(d) ? 'on_file' : parseContractorW9Status(d.w9Status),
    w9ReceivedAt: toDate(d.w9ReceivedAt),
    w9StoragePath: typeof d.w9StoragePath === 'string' ? d.w9StoragePath : undefined,
    agreementSignedAt: toDate(d.agreementSignedAt),
    allowedProjectIds: Array.isArray(d.allowedProjectIds)
      ? d.allowedProjectIds.map(String)
      : undefined,
    isActive: d.isActive !== false,
    updatedAt: toDate(d.updatedAt) ?? new Date(),
  };
}

function mapEntry(snap: { id: string; data: () => Record<string, unknown> }): ContractorTimeEntry {
  const d = snap.data();
  return {
    id: snap.id,
    contractorUserId: String(d.contractorUserId || ''),
    projectId: String(d.projectId || ''),
    taskId: typeof d.taskId === 'string' ? d.taskId : undefined,
    workDate: toDate(d.workDate) ?? new Date(),
    minutes: typeof d.minutes === 'number' ? d.minutes : 0,
    description: String(d.description || ''),
    hourlyRate: typeof d.hourlyRate === 'number' ? d.hourlyRate : undefined,
    billingRate: typeof d.billingRate === 'number' ? d.billingRate : undefined,
    billingUnit: d.billingUnit === 'day' || d.billingUnit === 'hour' ? d.billingUnit : undefined,
    timesheetId: typeof d.timesheetId === 'string' ? d.timesheetId : undefined,
    status: (d.status as ContractorTimeEntry['status']) || 'draft',
    dailyCapGrandfathered: d.dailyCapGrandfathered === true,
    enteredByUserId: typeof d.enteredByUserId === 'string' ? d.enteredByUserId : undefined,
    createdAt: toDate(d.createdAt) ?? new Date(),
    updatedAt: toDate(d.updatedAt),
  };
}

function mapTimesheet(snap: { id: string; data: () => Record<string, unknown> }): ContractorTimesheet {
  const d = snap.data();
  return {
    id: snap.id,
    contractorUserId: String(d.contractorUserId || ''),
    periodStart: toDate(d.periodStart) ?? new Date(),
    periodEnd: toDate(d.periodEnd) ?? new Date(),
    status: (d.status as ContractorTimesheet['status']) || 'draft',
    totalMinutes: typeof d.totalMinutes === 'number' ? d.totalMinutes : 0,
    totalAmount: typeof d.totalAmount === 'number' ? d.totalAmount : 0,
    projectIds: Array.isArray(d.projectIds) ? d.projectIds.map(String) : [],
    submittedAt: toDate(d.submittedAt),
    submittedByUserId: typeof d.submittedByUserId === 'string' ? d.submittedByUserId : undefined,
    invoiceId: typeof d.invoiceId === 'string' ? d.invoiceId : undefined,
    createdAt: toDate(d.createdAt) ?? new Date(),
    updatedAt: toDate(d.updatedAt),
  };
}

function mapInvoice(snap: { id: string; data: () => Record<string, unknown> }): ContractorInvoice {
  const d = snap.data();
  const lineItems = Array.isArray(d.lineItems) ? d.lineItems : [];
  return {
    id: snap.id,
    contractorUserId: String(d.contractorUserId || ''),
    contractorName: String(d.contractorName || ''),
    timesheetId: String(d.timesheetId || ''),
    invoiceNumber: String(d.invoiceNumber || ''),
    lineItems: lineItems.map((row: Record<string, unknown>) => ({
      projectId: String(row.projectId || ''),
      projectName: String(row.projectName || ''),
      projectNumber: typeof row.projectNumber === 'string' ? row.projectNumber : undefined,
      minutes: typeof row.minutes === 'number' ? row.minutes : 0,
      rate: typeof row.rate === 'number' ? row.rate : 0,
      amount: typeof row.amount === 'number' ? row.amount : 0,
      billingUnit: row.billingUnit === 'day' || row.billingUnit === 'hour' ? row.billingUnit : undefined,
      days: typeof row.days === 'number' ? row.days : undefined,
    })),
    totalAmount: typeof d.totalAmount === 'number' ? d.totalAmount : 0,
    billingUnit: d.billingUnit === 'day' || d.billingUnit === 'hour' ? d.billingUnit : undefined,
    appliedRate: typeof d.appliedRate === 'number' ? d.appliedRate : undefined,
    periodStart: toDate(d.periodStart) ?? new Date(),
    periodEnd: toDate(d.periodEnd) ?? new Date(),
    status: (d.status as ContractorInvoice['status']) || 'pending_payment',
    paidAt: toDate(d.paidAt),
    paidByUserId: typeof d.paidByUserId === 'string' ? d.paidByUserId : undefined,
    qbReferenceNote: typeof d.qbReferenceNote === 'string' ? d.qbReferenceNote : undefined,
    createdAt: toDate(d.createdAt) ?? new Date(),
    updatedAt: toDate(d.updatedAt),
  };
}

/** Monday 00:00 local for the week containing `date`. */
export function startOfContractorWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfContractorWeek(weekStart: Date): Date {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function formatWeekRange(weekStart: Date): string {
  const end = endOfContractorWeek(weekStart);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const y = weekStart.getFullYear() !== end.getFullYear();
  const startStr = weekStart.toLocaleDateString(undefined, y ? { ...opts, year: 'numeric' } : opts);
  const endStr = end.toLocaleDateString(undefined, { ...opts, year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

export function formatContractorSubmittedAt(submittedAt: Date | undefined): string {
  if (!submittedAt) return '—';
  return submittedAt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function isCurrentContractorWeek(weekStart: Date, now: Date = new Date()): boolean {
  return startOfContractorWeek(now).getTime() === startOfContractorWeek(weekStart).getTime();
}

export async function getContractorProfile(userId: string): Promise<ContractorProfile | null> {
  const snap = await getDoc(doc(db, PROFILES, userId));
  if (!snap.exists()) return null;
  return mapProfile(snap as { id: string; data: () => Record<string, unknown> });
}

export function subscribeContractorProfile(
  userId: string,
  onData: (profile: ContractorProfile | null) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, PROFILES, userId),
    (snap) => {
      onData(snap.exists() ? mapProfile(snap as { id: string; data: () => Record<string, unknown> }) : null);
    },
    onError,
  );
}

/** All contractor billing profiles (1099 pool day/hourly rates). */
export function subscribeContractorProfiles(
  onData: (profiles: Map<string, ContractorProfile>) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, PROFILES),
    (snap) => {
      const map = new Map<string, ContractorProfile>();
      snap.docs.forEach((d) => {
        map.set(d.id, mapProfile(d as { id: string; data: () => Record<string, unknown> }));
      });
      onData(map);
    },
    onError,
  );
}

export async function upsertContractorProfile(
  userId: string,
  patch: Partial<Omit<ContractorProfile, 'userId' | 'updatedAt'>>,
): Promise<void> {
  const ref = doc(db, PROFILES, userId);
  const existing = await getDoc(ref);
  const base = existing.exists()
    ? {}
    : {
        userId,
        defaultHourlyRate: 0,
        w9Status: 'missing' as const,
        isActive: true,
      };
  await setDoc(
    ref,
    removeUndefinedDeep({
      ...base,
      ...patch,
      userId,
      updatedAt: Timestamp.now(),
    }),
    { merge: true },
  );
}

export async function saveContractorDraftEntry(
  entry: Omit<ContractorTimeEntry, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'hourlyRate' | 'timesheetId'>,
): Promise<string> {
  const ref = await addDoc(collection(db, ENTRIES), removeUndefinedDeep({
    ...entry,
    status: 'draft',
    workDate: Timestamp.fromDate(entry.workDate),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  }));
  return ref.id;
}

export async function updateContractorDraftEntry(
  entryId: string,
  patch: Partial<Pick<ContractorTimeEntry, 'projectId' | 'workDate' | 'minutes' | 'description'>>,
): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: Timestamp.now() };
  if (patch.projectId) payload.projectId = patch.projectId;
  if (patch.minutes != null) payload.minutes = patch.minutes;
  if (patch.description != null) payload.description = patch.description;
  if (patch.workDate) payload.workDate = Timestamp.fromDate(patch.workDate);
  await updateDoc(doc(db, ENTRIES, entryId), payload as Record<string, any>);
}

export async function deleteContractorDraftEntry(entryId: string): Promise<void> {
  await deleteDoc(doc(db, ENTRIES, entryId));
}

export function subscribeContractorWeekEntries(
  contractorUserId: string,
  weekStart: Date,
  weekEnd: Date,
  onData: (entries: ContractorTimeEntry[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, ENTRIES),
    where('contractorUserId', '==', contractorUserId),
    where('workDate', '>=', Timestamp.fromDate(weekStart)),
    where('workDate', '<=', Timestamp.fromDate(weekEnd)),
    orderBy('workDate', 'asc'),
  );
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => mapEntry(d as { id: string; data: () => Record<string, unknown> })));
  });
}

export function subscribeContractorTimesheets(
  contractorUserId: string,
  onData: (rows: ContractorTimesheet[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, TIMESHEETS),
    where('contractorUserId', '==', contractorUserId),
    orderBy('periodStart', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => mapTimesheet(d as { id: string; data: () => Record<string, unknown> })));
  });
}

/** Submitted/invoiced/paid entries — used for submission history day breakdowns. */
export function subscribeContractorSubmittedEntries(
  contractorUserId: string,
  onData: (entries: ContractorTimeEntry[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, ENTRIES),
    where('contractorUserId', '==', contractorUserId),
    where('status', 'in', ['submitted', 'invoiced', 'paid']),
    orderBy('workDate', 'asc'),
  );
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => mapEntry(d as { id: string; data: () => Record<string, unknown> })));
  });
}

export function subscribeAllContractorInvoices(
  onData: (rows: ContractorInvoice[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const q = query(collection(db, INVOICES), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      onData(snap.docs.map((d) => mapInvoice(d as { id: string; data: () => Record<string, unknown> })));
    },
    onError,
  );
}

/** All draft contractor hours — visible to payables managers via Firestore rules. */
export function subscribeAllPendingDraftEntries(
  onData: (entries: ContractorTimeEntry[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const q = query(collection(db, ENTRIES), where('status', '==', 'draft'));
  return onSnapshot(
    q,
    (snap) => {
      onData(snap.docs.map((d) => mapEntry(d as { id: string; data: () => Record<string, unknown> })));
    },
    onError,
  );
}

export type PendingContractorWeekGroup = {
  contractorUserId: string;
  weekStart: Date;
  weekEnd: Date;
  totalMinutes: number;
  entryCount: number;
};

/** Group draft entries by contractor + Mon–Sun week (local calendar). */
export function groupPendingDraftsByWeek(entries: ContractorTimeEntry[]): PendingContractorWeekGroup[] {
  const map = new Map<string, PendingContractorWeekGroup>();
  for (const e of entries) {
    if (e.status !== 'draft' || e.minutes <= 0) continue;
    const weekStart = startOfContractorWeek(e.workDate);
    const key = `${e.contractorUserId}|${weekStart.getTime()}`;
    const row = map.get(key) || {
      contractorUserId: e.contractorUserId,
      weekStart,
      weekEnd: endOfContractorWeek(weekStart),
      totalMinutes: 0,
      entryCount: 0,
    };
    row.totalMinutes += e.minutes;
    row.entryCount += 1;
    map.set(key, row);
  }
  return Array.from(map.values()).sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
}

function toDateInputLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Human-readable message from a Firebase callable / HttpsError. */
export function formatCallableError(err: unknown): string {
  if (err && typeof err === 'object') {
    const fe = err as { code?: string; message?: string };
    const msg = String(fe.message || '').trim();
    if (msg) {
      if (msg.includes('W-9')) return 'W-9 must be on file before submitting. Ask an admin to upload it.';
      if (msg.includes('hourly rate')) return 'Contractor hourly rate is not configured. Set it in User Management.';
      if (msg.includes('No draft hours')) return 'No draft hours found for this week. Save hours on the timesheet first.';
      if (msg.includes('permission-denied') || fe.code === 'functions/permission-denied') {
        return 'You do not have permission to submit for this contractor. Ensure your account has Executive Assistant designation in User Management.';
      }
      return msg;
    }
    if (fe.code) return fe.code.replace('functions/', '');
  }
  return err instanceof Error ? err.message : 'Request failed';
}

export type SubmitContractorTimesheetInput = {
  contractorUserId?: string;
  periodStart: string;
  periodEnd: string;
  billingUnit?: 'hour' | 'day';
  rate?: number;
};

export type SubmitContractorTimesheetResult = {
  timesheetId: string;
  invoiceId: string;
  invoiceNumber: string;
  totalAmount: number;
};

export async function submitContractorTimesheet(
  input: SubmitContractorTimesheetInput,
): Promise<SubmitContractorTimesheetResult> {
  const fns = getFunctions(app, 'us-central1');
  const fn = httpsCallable<SubmitContractorTimesheetInput, SubmitContractorTimesheetResult>(
    fns,
    'submitContractorTimesheet',
  );
  const res = await fn(input);
  return res.data;
}

/** Convenience wrapper for payables inbox — submit a grouped pending week. */
export async function submitPendingContractorWeek(
  group: PendingContractorWeekGroup,
  billing?: Pick<SubmitContractorTimesheetInput, 'billingUnit' | 'rate'>,
): Promise<SubmitContractorTimesheetResult> {
  return submitContractorTimesheet({
    contractorUserId: group.contractorUserId,
    periodStart: toDateInputLocal(group.weekStart),
    periodEnd: toDateInputLocal(group.weekEnd),
    ...billing,
  });
}

export async function markContractorInvoicePaid(
  invoiceId: string,
  qbReferenceNote?: string,
): Promise<void> {
  const fns = getFunctions(app, 'us-central1');
  const fn = httpsCallable<{ invoiceId: string; qbReferenceNote?: string }, { ok: boolean }>(
    fns,
    'markContractorInvoicePaid',
  );
  await fn({ invoiceId, qbReferenceNote });
}

export async function deleteContractorInvoice(
  invoiceId: string,
): Promise<{ entryCount: number }> {
  const fns = getFunctions(app, 'us-central1');
  const fn = httpsCallable<{ invoiceId: string }, { ok: boolean; entryCount: number }>(
    fns,
    'deleteContractorInvoice',
  );
  const { data } = await fn({ invoiceId });
  return { entryCount: data?.entryCount ?? 0 };
}

export async function listPaidInvoicesYtd(year: number): Promise<ContractorInvoice[]> {
  const start = new Date(year, 0, 1);
  const snap = await getDocs(
    query(
      collection(db, INVOICES),
      where('status', '==', 'paid'),
      where('paidAt', '>=', Timestamp.fromDate(start)),
      orderBy('paidAt', 'desc'),
    ),
  );
  return snap.docs.map((d) => mapInvoice(d as { id: string; data: () => Record<string, unknown> }));
}

export async function uploadContractorW9(userId: string, file: File): Promise<string> {
  const max = 15 * 1024 * 1024;
  if (file.size > max) throw new Error('W-9 must be 15 MB or smaller');
  const safeName = file.name.replace(/[^\w.\-]+/g, '_');
  const path = `contractor-w9/${userId}/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  await upsertContractorProfile(userId, {
    w9Status: 'on_file',
    w9ReceivedAt: new Date(),
    w9StoragePath: path,
  });
  return path;
}

export async function getContractorW9DownloadUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath));
}
